<template>
  <div class="profile-page">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">ユーザープロフィール</h1>
      </div>
    </div>

    <section class="section">
      <div class="container">
        <div v-if="isLoadingProfile" class="loading-message">
          プロフィールを読み込み中...
        </div>
        <div v-else-if="profileError" class="error-message">
          {{ profileError }}
        </div>
        <div v-else-if="currentUser" class="profile-container">
          
          <!-- User Info Section -->
          <div class="profile-section">
            <h3>基本情報</h3>
            <p><strong>ユーザーID:</strong> {{ currentUser.id }}</p>
            <p><strong>ロール:</strong> {{ currentUser.role === 'admin' ? '管理者' : '一般ユーザー' }}</p>
          </div>

          <!-- Nickname Section -->
          <div class="profile-section">
            <h3>ニックネーム</h3>
            <div v-if="!isEditingNickname">
              <p>{{ userProfile.nickname || 'ニックネームが設定されていません' }}</p>
              <button @click="isEditingNickname = true" class="btn btn-secondary">編集</button>
            </div>
            <div v-else class="nickname-edit">
              <input type="text" v-model="editableNickname" placeholder="ニックネームを入力" class="nickname-input">
              <div class="nickname-actions">
                <button @click="saveNickname" class="btn btn-primary" :disabled="isSavingNickname">
                  {{ isSavingNickname ? '保存中...' : '保存' }}
                </button>
                <button @click="cancelEditNickname" class="btn btn-secondary">キャンセル</button>
              </div>
              <p v-if="nicknameSuccess" class="success-message small">{{ nicknameSuccess }}</p>
              <p v-if="nicknameError" class="error-message small">{{ nicknameError }}</p>
            </div>
          </div>

          <!-- Email Section -->
          <div class="profile-section">
            <h3>メールアドレス</h3>
            <p>{{ currentUser.email }}</p>
            <p v-if="!currentUser.email_verified" class="email-status not-verified">
              （メールアドレス未確認 - <button class="btn-link" @click="resendVerificationEmail">確認メールを再送信</button>）
            </p>
             <p v-if="currentUser.email_verified" class="email-status verified">
               （メールアドレス確認済み）
             </p>
             <p v-if="emailMessage" :class="emailError ? 'error-message small' : 'success-message small'">
               {{ emailMessage }}
             </p>
          </div>

          <!-- Password Section -->
          <div class="profile-section">
            <h3>パスワード変更</h3>
             <form @submit.prevent="changePassword" class="password-change-form">
               <div class="form-group">
                 <label for="newPassword">新しいパスワード</label>
                 <input type="password" id="newPassword" v-model="newPassword" required placeholder="新しいパスワード（6文字以上）">
               </div>
               <div class="form-group">
                 <label for="confirmNewPassword">新しいパスワード（確認）</label>
                 <input type="password" id="confirmNewPassword" v-model="confirmNewPassword" required placeholder="新しいパスワードを再入力">
               </div>
               <button type="submit" class="btn btn-primary" :disabled="isChangingPassword">
                  {{ isChangingPassword ? '変更中...' : 'パスワードを変更' }}
                </button>
                <p v-if="passwordSuccess" class="success-message small">{{ passwordSuccess }}</p>
                <p v-if="passwordError" class="error-message small">{{ passwordError }}</p>
                <p class="info-text small">セキュリティのため、パスワード変更には最近のログインが必要な場合があります。</p>
             </form>
          </div>

          <!-- Course Enrollment Section -->
          <div class="profile-section">
            <h3>コース申し込み履歴</h3>
            <div v-if="isLoadingEnrollments" class="loading-state">
              申し込み履歴を読み込み中...
            </div>
            <div v-else-if="enrollmentError" class="error-message">
              {{ enrollmentError }}
            </div>
            <div v-else-if="enrollments.length === 0" class="empty-state">
              コースの申し込み履歴はありません。
              <br>
              <router-link to="/courses" class="btn btn-primary" style="margin-top: 15px;">
                コース一覧を見る
              </router-link>
            </div>
            <div v-else class="enrollments-list">
              <div 
                v-for="enrollment in enrollments" 
                :key="enrollment.id"
                class="enrollment-item"
                :class="'status-' + enrollment.status"
              >
                <div class="enrollment-header">
                  <h4 class="course-name">{{ enrollment.course_name }}</h4>
                  <span class="enrollment-status" :class="'status-' + enrollment.status">
                    {{ getStatusDisplayName(enrollment.status) }}
                  </span>
                </div>
                <div class="enrollment-details">
                  <p><strong>料金:</strong> ¥{{ enrollment.price }}</p>
                  <p><strong>申し込み日:</strong> {{ formatDate(enrollment.created_at) }}</p>
                  <p v-if="enrollment.message"><strong>メッセージ:</strong> {{ enrollment.message }}</p>
                  <p v-if="enrollment.admin_notes" class="admin-notes">
                    <strong>管理者からのメッセージ:</strong> {{ enrollment.admin_notes }}
                  </p>
                </div>
              </div>
            </div>
            <div class="enrollment-actions">
              <button @click="loadEnrollments" class="btn btn-outline" :disabled="isLoadingEnrollments">
                <i class="fas fa-sync-alt"></i>
                {{ isLoadingEnrollments ? '更新中...' : '履歴を更新' }}
              </button>
            </div>
          </div>

        </div>
        <div v-else>
           <p>ログインしていません。 <router-link to="/auth">ログイン</router-link> してください。</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { authService } from '@/api';
import { getUserEnrollments, getStatusDisplayName } from '@/api/courseEnrollment';

const currentUser = ref(null);
const userProfile = ref({});
const isLoadingProfile = ref(true);
const profileError = ref('');

// Nickname state
const isEditingNickname = ref(false);
const editableNickname = ref('');
const isSavingNickname = ref(false);
const nicknameError = ref('');
const nicknameSuccess = ref('');

// Password state
const newPassword = ref('');
const confirmNewPassword = ref('');
const isChangingPassword = ref(false);
const passwordError = ref('');
const passwordSuccess = ref('');

// Email state
const emailMessage = ref('');
const emailError = ref(false);

// Course Enrollment state
const enrollments = ref([]);
const isLoadingEnrollments = ref(false);
const enrollmentError = ref('');

// 获取用户信息
const fetchUserInfo = async () => {
  isLoadingProfile.value = true;
  profileError.value = '';
  
  try {
    // 检查是否已登录
    if (!authService.isLoggedIn()) {
      currentUser.value = null;
      isLoadingProfile.value = false;
      return;
    }
    
    // 从后端获取最新的用户信息
    const user = await authService.getCurrentUser();
    currentUser.value = user;
    
    // 这里可以获取用户的其他信息，比如昵称等
    // 暂时使用空对象
    userProfile.value = {};
    editableNickname.value = '';
    
    // 加载课程报名记录
    await loadEnrollments();
    
  } catch (error) {
    console.error("用户信息获取错误:", error);
    profileError.value = "ユーザー情報の読み込みに失敗しました。";
    
    // 如果获取用户信息失败，可能是token过期，清除登录状态
    if (error.message && error.message.includes('401')) {
      authService.logout();
      currentUser.value = null;
    }
  } finally {
    isLoadingProfile.value = false;
  }
};

// 加载课程报名记录
const loadEnrollments = async () => {
  if (!currentUser.value) return;
  
  isLoadingEnrollments.value = true;
  enrollmentError.value = '';
  
  try {
    const response = await getUserEnrollments();
    if (response.success) {
      enrollments.value = response.data;
    } else {
      throw new Error(response.message || '申し込み履歴の読み込みに失敗しました');
    }
  } catch (error) {
    console.error('申し込み履歴読み込みエラー:', error);
    enrollmentError.value = '申し込み履歴の読み込みに失敗しました。';
  } finally {
    isLoadingEnrollments.value = false;
  }
};

// 日期格式化
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
};

// 组件挂载时获取用户信息
onMounted(() => {
  fetchUserInfo();
});

// --- Nickname Logic --- 
const saveNickname = async () => {
  if (!currentUser.value) return;
  isSavingNickname.value = true;
  nicknameError.value = '';
  nicknameSuccess.value = '';

  try {
    // 这里应该调用保存昵称的API
    // 暂时模拟保存成功
    userProfile.value.nickname = editableNickname.value;
    isEditingNickname.value = false;
    nicknameSuccess.value = 'ニックネームを更新しました！';
    setTimeout(() => nicknameSuccess.value = '', 3000);
  } catch (error) {
    console.error("ニックネーム保存エラー:", error);
    nicknameError.value = "ニックネームの保存に失敗しました。";
     setTimeout(() => nicknameError.value = '', 3000);
  } finally {
    isSavingNickname.value = false;
  }
};

const cancelEditNickname = () => {
  editableNickname.value = userProfile.value.nickname || '';
  isEditingNickname.value = false;
  nicknameError.value = '';
  nicknameSuccess.value = '';
};

// --- Password Logic --- 
const changePassword = async () => {
  if (!currentUser.value) return;
  passwordError.value = '';
  passwordSuccess.value = '';

  if (newPassword.value.length < 6) {
    passwordError.value = '新しいパスワードは6文字以上で入力してください。';
    return;
  }
  if (newPassword.value !== confirmNewPassword.value) {
    passwordError.value = '新しいパスワードが一致しません。';
    return;
  }

  isChangingPassword.value = true;
  try {
    await authService.changePassword(newPassword.value);
    passwordSuccess.value = 'パスワードが正常に変更されました。';
    newPassword.value = '';
    confirmNewPassword.value = '';
     setTimeout(() => passwordSuccess.value = '', 3000);
  } catch (error) {
    console.error("パスワード変更エラー:", error);
    passwordError.value = 'パスワードの変更に失敗しました。' + (error.message || '');
     setTimeout(() => passwordError.value = '', 5000);
  } finally {
    isChangingPassword.value = false;
  }
};

// --- Email Verification Logic ---
const resendVerificationEmail = async () => {
   if (!currentUser.value) return;
   emailMessage.value = '';
   emailError.value = false;
   try {
     await authService.resendVerification(currentUser.value.email);
     emailMessage.value = '確認メールを再送信しました。メールボックスをご確認ください。';
   } catch (error) {
     console.error("確認メール再送信エラー:", error);
     emailMessage.value = '確認メールの再送信に失敗しました。';
     emailError.value = true;
   }
   setTimeout(() => emailMessage.value = '', 5000);
 };

</script>

<style scoped>
/* Basic Page Styles */
.profile-page {
  padding-bottom: 50px;
}

.page-header {
  background-color: var(--secondary-color); /* Different color? */
  color: white;
  padding: 50px 0;
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  font-size: 28px;
  font-weight: bold;
}

.profile-container {
  max-width: 700px;
  margin: 0 auto;
  background-color: #fff;
  padding: 30px 40px;
  border-radius: 10px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.07);
}

.profile-section {
  margin-bottom: 35px;
  padding-bottom: 25px;
  border-bottom: 1px solid #eee;
}
.profile-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.profile-section h3 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 18px;
  color: var(--primary-color);
  border-left: 4px solid var(--primary-color);
  padding-left: 10px;
}

/* Nickname Display/Edit */
.nickname-display {
  display: flex;
  align-items: center;
  gap: 15px;
}
.nickname-display span {
  font-size: 16px;
  color: #333;
}

.nickname-edit input {
  width: calc(100% - 110px); /* Adjust based on button widths */
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 15px;
  margin-right: 10px;
}
.nickname-edit .form-actions {
   display: inline-block;
}

/* Course Enrollments */
.enrollments-list {
  margin-bottom: 20px;
}

.enrollment-item {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  border-left: 4px solid #ddd;
  transition: all 0.3s ease;
}

.enrollment-item.status-pending {
  border-left-color: #ff9800;
  background-color: #fff3e0;
}

.enrollment-item.status-approved {
  border-left-color: #4caf50;
  background-color: #f1f8e9;
}

.enrollment-item.status-rejected {
  border-left-color: #f44336;
  background-color: #ffebee;
}

.enrollment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.course-name {
  margin: 0;
  color: var(--primary-color);
  font-size: 18px;
}

.enrollment-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.enrollment-status.status-pending {
  background-color: #ff9800;
  color: white;
}

.enrollment-status.status-approved {
  background-color: #4caf50;
  color: white;
}

.enrollment-status.status-rejected {
  background-color: #f44336;
  color: white;
}

.enrollment-details p {
  margin: 8px 0;
  color: #555;
}

.admin-notes {
  background-color: rgba(255, 139, 171, 0.1);
  padding: 10px;
  border-radius: 4px;
  border-left: 3px solid var(--primary-color);
  margin-top: 10px;
}

.enrollment-actions {
  text-align: center;
  margin-top: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  background: #f9f9f9;
  border-radius: 8px;
}

.loading-state {
  text-align: center;
  padding: 20px;
  color: #666;
}

/* General Form Styles */
.form-group {
  margin-bottom: 15px;
}
.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
}
.form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 15px;
  box-sizing: border-box;
}

.btn {
  /* Basic button styling from other components */
   padding: 10px 20px;
   border-radius: 6px;
   font-weight: 500;
   cursor: pointer;
   transition: all 0.3s ease;
   text-align: center;
   border: none;
   font-size: 15px;
   text-decoration: none;
   display: inline-block;
}
.btn-primary { background-color: var(--primary-color); color: white; }
.btn-primary:hover { background-color: var(--secondary-color); }
.btn-primary:disabled { background-color: #ccc; cursor: not-allowed; }
.btn-outline { background-color: transparent; color: var(--primary-color); border: 1px solid var(--primary-color); }
.btn-outline:hover { background-color: var(--primary-color); color: white; }
.btn-outline:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-sm { padding: 6px 12px; font-size: 13px; }
.btn-link { background: none; border: none; color: var(--primary-color); cursor: pointer; text-decoration: underline; font-size: 14px; padding: 0; }
.btn-link:hover { color: var(--secondary-color); }

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px; /* Add margin for nickname edit */
}

/* Messages */
.error-message,
.success-message {
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 10px;
  font-size: 14px;
}
.error-message { background-color: #ffebee; color: #c62828; }
.success-message { background-color: #e8f5e9; color: #2e7d32; }
.error-message.small, .success-message.small {
    padding: 5px 10px;
    font-size: 13px;
    margin-top: 8px;
}

.email-status {
    font-size: 13px;
    margin-top: 5px;
}
.email-status.verified { color: #2e7d32; }
.email-status.not-verified { color: #c62828; }
.email-status .btn-link { font-size: 13px; vertical-align: baseline; }

.info-text.small {
    font-size: 12px;
    color: #888;
    margin-top: 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .profile-container { padding: 20px 25px; }
  .nickname-edit input { width: 100%; margin-right: 0; margin-bottom: 10px; }
  .nickname-edit .form-actions { display: flex; }
  
  .enrollment-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .enrollment-item {
    padding: 15px;
  }
}
</style> 